import { Switch, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { DrmJednotkaResponse } from '../../../common/drmTypes'
import { useAppSelector } from '../../../redux/hooks'
import type { LocalitySummary } from '../../../redux/propertyPriceSlice'
import { numberSorter, stringSorter } from '../../utils/generalTableHelpers'
import {
  priceRenderer,
  unitPriceCoverageRenderer,
  unitPriceDeltaRenderer,
} from '../../utils/propertyDetailPriceHelpers'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useTableLocale } from '../../utils/useTableLocale'
import { PropertyDetailPriceUnitSaleTableSummary } from '../PropertyDetailPriceUnitSaleTableSummary/PropertyDetailPriceUnitSaleTableSummary'

export interface PropertyDetailPriceUnitSaleTableProps {
  area: number | null
}

interface SaleRow {
  key: string
  id: number
  name: string
  transactions: number
  m2: number
  total: number
  deviation: number | null
}

const noData = (
  <div className="my-2 text-yellow-600">
    Je nám líto, ale pro tuto lokalitu neevidujeme žádná data o prodejních
    cenách.
  </div>
)

const scrollX = { x: 'max-content' }

export const PropertyDetailPriceUnitSaleTable: FunctionComponent<PropertyDetailPriceUnitSaleTableProps> =
  ({ area }) => {
    const { searchFilter, moneyFilter, rangeFilter } =
      useColumnFilters<SaleRow>()

    const localities = useAppSelector(
      state => state.property.price.data?.saleOffer?.localities ?? []
    )
    const mainLocality = useAppSelector(
      state => state.property.price.data?.saleOffer?.mainLocality
    )
    const drmDetail = useAppSelector(
      state => state.property.drm.data as DrmJednotkaResponse | undefined
    )

    const [showUncovered, setShowUncovered] = useState(false)

    const saleLocalities = useMemo<LocalitySummary[]>(() => {
      if (showUncovered) return localities
      return localities.filter(locality => !!locality.transactionsCount)
    }, [localities, showUncovered])

    const deltaTooltip = useMemo(
      () => ({
        title: `Procentuální odchylka ceny v dané lokalitě od průměrné ceny v ${
          mainLocality?.name || 'nadřazeném územním celku'
        }`,
        placement: 'top' as const,
      }),
      [mainLocality?.name]
    )

    const dataSource = useMemo<SaleRow[]>(() => {
      const mainPriceM2 = mainLocality?.averagePriceM2
      return (
        saleLocalities?.map((locality: LocalitySummary) => {
          const localityPriceM2 = Number(locality.averagePriceM2 || 0)
          const deviation =
            mainPriceM2 && localityPriceM2
              ? ((localityPriceM2 - mainPriceM2) / mainPriceM2) * 100
              : null

          return {
            key: `${locality.type}-${locality.id}`,
            id: locality.id,
            name: locality.name,
            transactions: locality.transactionsCount,
            m2: localityPriceM2,
            total: localityPriceM2 * (area || 0),
            deviation,
          }
        }) || []
      )
    }, [area, saleLocalities, mainLocality])

    const columns = useMemo<ColumnsType<SaleRow>>(
      () => [
        {
          title: 'Lokalita',
          dataIndex: 'name',
          sorter: stringSorter('name'),
          ...searchFilter('name', 'Hledat v lokalitě'),
        },
        {
          title: '⌀ Cena',
          dataIndex: 'total',
          className: '!text-right',
          sorter: numberSorter('total'),
          ...moneyFilter('total'),
          render: priceRenderer,
        },
        {
          title: '⌀ Cena m²',
          dataIndex: 'm2',
          className: '!text-right',
          sorter: numberSorter('m2'),
          ...moneyFilter('m2'),
          render: priceRenderer,
        },
        {
          title: 'Δ',
          dataIndex: 'deviation',
          className: '!text-right',
          width: 45,
          sorter: numberSorter('deviation'),
          showSorterTooltip: deltaTooltip,
          render: unitPriceDeltaRenderer,
        },
        {
          title: 'Pokrytost',
          dataIndex: 'transactions',
          className: '!text-right',
          sorter: numberSorter('transactions'),
          ...rangeFilter('transactions'),
          render: unitPriceCoverageRenderer,
        },
      ],
      [deltaTooltip, moneyFilter, rangeFilter, searchFilter]
    )

    const rowClassName = useCallback(
      (record: SaleRow) => {
        if (!record?.name) return ''

        const cadastralName = drmDetail?.katastralniUzemi?.nazev
        const municipalityName = drmDetail?.vBudove?.castObce?.nazev

        const isMatch =
          (cadastralName && record.name.includes(cadastralName)) ||
          (municipalityName && record.name.includes(municipalityName))

        return isMatch
          ? 'bg-[#e1f0ee]/50'
          : !record?.transactions
          ? 'opacity-80'
          : ''
      },
      [drmDetail?.katastralniUzemi?.nazev, drmDetail?.vBudove?.castObce?.nazev]
    )

    const summary = useCallback(
      () => <PropertyDetailPriceUnitSaleTableSummary area={area} />,
      [area]
    )

    return (
      <div className="space-y-2 pb-8">
        <div className="flex justify-between items-center">
          <Typography.Title level={4} className="!mb-0">
            Prodejní (inzerované) ceny
          </Typography.Title>
          <div className="flex items-center space-x-2">
            <span>Zobrazit bez pokrytí</span>
            <Switch
              size="small"
              className="!flex"
              checked={showUncovered}
              onChange={setShowUncovered}
            />
          </div>
        </div>

        <Table
          size="small"
          rowKey="key"
          scroll={scrollX}
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowClassName={rowClassName}
          locale={useTableLocale(!localities.length, 5, noData)}
          summary={summary}
          showSorterTooltip={false}
        />
      </div>
    )
  }
